import React, { useEffect, useRef, useState } from "react";
import "../assets/css/reportTypeMenu.css";

/**
 * How the report below is grouped, behind one icon.
 *
 * This started as a panel sitting open above the document, which cost a
 * strip of the page permanently to answer a question you ask once and then
 * leave alone. Folded into a button: the report itself gets the room, and
 * the current choice is still readable from the report's own subtitle.
 *
 * Props:
 *   types    : [{ key, label, icon, tone }]
 *   value    : the selected key
 *   onChange : (key) => void
 *   actions? : [{ key, label, hint?, icon, tone?, busy?, onSelect }]
 *              Things that DO something rather than select something — a
 *              workbook to download, say. They sit in the same grid as the
 *              types, filling the cell the eight types leave empty; a stray
 *              row hanging below a rule read as an afterthought when there
 *              was an obvious hole right above it.
 *
 *              They still never carry a tick, and they carry a download arrow
 *              and a hint instead: a tile that looks exactly like the others
 *              but leaves the report unchanged reads as a control that failed.
 */
const ReportTypeMenu = ({
  types = [],
  value,
  onChange,
  actions = [],
  /* All / Pending Only. It used to be a bar of its own across the page; it
     sits at the top of this menu instead, because it and the grouping below
     are the same question asked twice — what goes into this report — and they
     were being answered a foot apart. */
  scope,
  onScopeChange,
  totalCount,
  pendingCount,
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const active = types.find((t) => t.key === value) ?? types[0];

  useEffect(() => {
    if (!open) return;

    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="rtm" ref={wrapRef}>
      <button
        type="button"
        className={`rtm__trigger${open ? " is-open" : ""}`}
        onClick={() => setOpen((s) => !s)}
        /* The report's own subtitle already says which grouping is on, so the
           button stays an icon. The tooltip covers the case where the report
           has scrolled out of view. */
        title={`Report type: ${active?.label ?? "—"}`}
        aria-label={`Report type: ${active?.label ?? ""}`}
        aria-expanded={open}
      >
        {/* A fixed icon, not the active type's. Mirroring the selection made
            the button a different picture on every page — a person on the
            User Report, a group on the Overall Report — so the one control
            never became recognisable. The tooltip and the report's own
            subtitle carry which type is on. */}
        <i className="bi bi-ui-checks-grid" />
        <i className="bi bi-chevron-down rtm__caret" />
      </button>

      {open && (
        <div className="rtm__pop" role="menu">
          <div className="rtm__head">
            <span className="rtm__title">REPORT TYPE</span>
            <span className="rtm__current">{active?.label}</span>
          </div>

          {onScopeChange && (
            <div
              className="rtm__scope"
              role="radiogroup"
              aria-label="Which tasks to include"
            >
              {[
                { key: "all", label: "All", icon: "bi-collection", count: totalCount },
                {
                  key: "pending",
                  label: "Pending Only",
                  icon: "bi-hourglass-split",
                  count: pendingCount,
                },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  role="radio"
                  aria-checked={scope === opt.key}
                  className={`rtm__scope-btn${scope === opt.key ? " is-active" : ""}`}
                  /* Closes, like the grouping tiles below.
                   *
                   * It used to stay open, on the reasoning that scope and
                   * grouping are usually changed together and shutting on the
                   * first of two clicks costs a reopen. That traded a small
                   * cost for a worse one: every scope change reloads the
                   * report, so the menu sat over a full-page loading skeleton
                   * with the answer forming underneath it, unreadable. One
                   * extra click is cheaper than hiding the thing the click was
                   * for — and the menu was already inconsistent, since picking
                   * a grouping closed it and picking a scope did not. */
                  onClick={() => {
                    onScopeChange(opt.key);
                    setOpen(false);
                  }}
                >
                  <i className={`bi ${opt.icon}`} />
                  <span>{opt.label}</span>
                  {Number.isFinite(opt.count) && (
                    <span className="rtm__scope-count">{opt.count}</span>
                  )}
                </button>
              ))}
            </div>
          )}

          <div className="rtm__grid">
            {types.map((t) => (
              <button
                key={t.key}
                type="button"
                role="menuitemradio"
                aria-checked={t.key === value}
                className={`rtm__tile${t.key === value ? " is-active" : ""}`}
                onClick={() => {
                  onChange?.(t.key);
                  setOpen(false);
                }}
              >
                <span className={`rtm__icon rtm__icon--${t.tone || "blue"}`}>
                  <i className={`bi ${t.icon}`} />
                </span>
                <span className="rtm__label">{t.label}</span>
                {t.key === value && (
                  <i className="bi bi-check2 rtm__check" aria-hidden="true" />
                )}
              </button>
            ))}

            {/* Same grid, so the download lands in the cell the types leave
                empty rather than in a row of its own underneath. */}
            {actions.map((act) => (
              <button
                key={act.key}
                type="button"
                role="menuitem"
                className="rtm__tile rtm__tile--action"
                title={act.hint || act.label}
                disabled={act.busy}
                onClick={async () => {
                  setOpen(false);
                  await act.onSelect?.();
                }}
              >
                <span className={`rtm__icon rtm__icon--${act.tone || "green"}`}>
                  <i className={`bi ${act.busy ? "bi-hourglass-split" : act.icon}`} />
                </span>
                <span className="rtm__action-text">
                  <span className="rtm__label">{act.label}</span>
                  {act.hint && <span className="rtm__hint">{act.hint}</span>}
                </span>
                <i className="bi bi-download rtm__go" aria-hidden="true" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportTypeMenu;
